import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';

type ApplicationStatus = 'new' | 'reviewing' | 'approved' | 'rejected';

interface PartnerApplication {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  status: ApplicationStatus;
  notes: string | null;
  created_at: string;
}

const STATUS_STYLES: Record<ApplicationStatus, string> = {
  new: 'bg-teal-10 text-teal-50',
  reviewing: 'bg-orange-10 text-orange-70',
  approved: 'bg-success-5 text-success-70',
  rejected: 'bg-destructive-5 text-red-600',
};

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function PartnerApplications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applications, setApplications] = useState<PartnerApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState<ApplicationStatus>('new');
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<ApplicationStatus | 'all'>('all');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    checkAdminAndLoad();
  }, [user, navigate]);

  const checkAdminAndLoad = async () => {
    if (!user?.id) return;
    try {
      const isAdmin = await isUserAdmin(user.id);
      if (!isAdmin) { navigate('/login'); return; }
      await loadApplications();
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadApplications = async () => {
    const { data, error } = await supabase
      .from('partner_applications')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setApplications(data as PartnerApplication[]);
  };

  const startEdit = (app: PartnerApplication) => {
    setEditingId(app.id);
    setEditNotes(app.notes || '');
    setEditStatus(app.status);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    const { error } = await supabase
      .from('partner_applications')
      .update({ status: editStatus, notes: editNotes || null, updated_at: new Date().toISOString() })
      .eq('id', editingId);
    if (!error) {
      setApplications((prev) =>
        prev.map((a) => a.id === editingId ? { ...a, status: editStatus, notes: editNotes || null } : a)
      );
    }
    setSaving(false);
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const filtered = filterStatus === 'all'
    ? applications
    : applications.filter((a) => a.status === filterStatus);

  const counts = {
    new: applications.filter((a) => a.status === 'new').length,
    reviewing: applications.filter((a) => a.status === 'reviewing').length,
    approved: applications.filter((a) => a.status === 'approved').length,
    rejected: applications.filter((a) => a.status === 'rejected').length,
  };

  if (isLoading) return <LoadingSpinner message="Loading partner applications..." />;

  return (
    <div className="min-h-screen bg-brand-5 font-urbanist">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to="/admin" className="text-chocolate-400 hover:text-chocolate-700 text-sm transition-colors">
            ← Admin
          </Link>
          <span className="text-chocolate-300">/</span>
          <h1 className="text-text-xl text-chocolate-800">Partner Applications</h1>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {(Object.entries(counts) as [ApplicationStatus, number][]).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setFilterStatus(filterStatus === status ? 'all' : status)}
              className={`rounded-2xl border p-4 text-left transition-all ${
                filterStatus === status
                  ? 'border-chocolate-400 bg-white shadow-soft'
                  : 'border-chocolate-100 bg-white hover:border-chocolate-300'
              }`}
            >
              <p className="text-2xl font-bold text-chocolate-800 mb-0.5">{count}</p>
              <p className="text-xs font-semibold text-chocolate-400 capitalize">{status}</p>
            </button>
          ))}
        </div>

        {/* Filter label */}
        {filterStatus !== 'all' && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-chocolate-500">Filtering by:</span>
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${STATUS_STYLES[filterStatus]}`}>{filterStatus}</span>
            <button onClick={() => setFilterStatus('all')} className="text-xs text-chocolate-400 hover:text-chocolate-700 underline">clear</button>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-chocolate-100 bg-white p-12 text-center">
            <p className="text-chocolate-400">No applications {filterStatus !== 'all' ? `with status "${filterStatus}"` : 'yet'}.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((app) => (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-chocolate-100 bg-white shadow-soft p-5"
              >
                {editingId === app.id ? (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <p className="font-semibold text-chocolate-800">{app.full_name}</p>
                        <p className="text-sm text-chocolate-400">{app.email}{app.phone ? ` · ${app.phone}` : ''}</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-chocolate-600 mb-1">Status</label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value as ApplicationStatus)}
                        className="w-full sm:w-48 px-3 py-2 text-sm border border-chocolate-200 rounded-xl bg-cream-50 text-chocolate-800 focus:outline-none focus:ring-2 focus:ring-sage-400"
                      >
                        <option value="new">New</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-chocolate-600 mb-1">Notes</label>
                      <textarea
                        rows={3}
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Internal notes…"
                        className="w-full px-3 py-2 text-sm border border-chocolate-200 rounded-xl bg-cream-50 text-chocolate-800 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400 resize-none"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={saving}
                        className="px-4 py-2 bg-chocolate-800 hover:bg-chocolate-900 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 bg-cream-200 hover:bg-cream-300 text-chocolate-700 text-sm font-semibold rounded-xl transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-chocolate-800">{app.full_name}</p>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[app.status]}`}>
                          {app.status}
                        </span>
                      </div>
                      <p className="text-sm text-chocolate-500">
                        {app.email}
                        {app.phone ? <> &middot; {app.phone}</> : null}
                      </p>
                      {app.notes && (
                        <p className="text-xs text-chocolate-400 mt-1.5 italic">{app.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-chocolate-400">{formatDate(app.created_at)}</span>
                      <button
                        onClick={() => startEdit(app)}
                        className="px-3 py-1.5 bg-cream-200 hover:bg-cream-300 text-chocolate-700 text-xs font-semibold rounded-lg transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
